import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';

import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import {
  EstoqueLoteSerieInlineComponent,
  LoteEntradaEstoqueModalItem,
  SerieEntradaEstoqueModalItem,
} from '../../../../../shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { LoteSaidaEstoqueModalItem } from '../../../../../shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalItem } from '../../../../../shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { OrdemProducaoDetalheStruct } from '../../../../../shared/services/structs/suprimentos/ordem-producao-detalhe.struct';
import { OrdemProducaoItemStruct } from '../../../../../shared/services/structs/suprimentos/ordem-producao-item.struct';
import { LancarEstoqueOrdemProducaoItemRequest } from '../../../../../shared/services/requests/suprimentos/lancar-estoque-ordem-producao-item.request';

export interface LancarEstoqueOrdemProducaoDialogData {
  ordem: OrdemProducaoDetalheStruct;
}

interface InsumoLancamentoRow {
  item: OrdemProducaoItemStruct;
  idLocalizacaoEstoque: number | null;
  itensRastreabilidade: (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[];
  rastreabilidadeCompleta: boolean;
}

/**
 * Lançamento de estoque individual de UMA OP (seção 5.1 da US#141200). Monta o request como uma
 * lista de 1 item — o mesmo formato que o lançamento em lote da Lista (seção 5.3) usa, mesmo
 * endpoint pros dois.
 */
@Component({
  selector: 'app-lancar-estoque-ordem-producao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AbntPipe,
    AlertCardComponent,
    EstoqueLoteSerieInlineComponent,
  ],
  templateUrl: './lancar-estoque-ordem-producao-dialog.component.html',
  styleUrl: './lancar-estoque-ordem-producao-dialog.component.css',
})
export class LancarEstoqueOrdemProducaoDialogComponent implements OnInit {
  public isSalvando = false;
  public localizacoes: any[] = [];
  public idLocalizacaoEstoqueEntrada: number | null = null;

  public quantidadeEntrada = 0;
  public justificativaAjusteEntrada = '';

  public linhas: InsumoLancamentoRow[] = [];
  public readonly displayedColumns = ['item', 'movimento', 'estoqueAtual', 'apos', 'localizacaoSaida'];

  public codigoLoteEntrada = '';
  public dataFabricacaoLoteEntrada: Date | null = null;
  public dataValidadeLoteEntrada: Date | null = null;

  public itensEntradaSerie: SerieEntradaEstoqueModalItem[] = [];
  public serieEntradaCompleta = true;
  public abertoEntrada = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LancarEstoqueOrdemProducaoDialogData,
    public dialogRef: MatDialogRef<LancarEstoqueOrdemProducaoDialogComponent>,
    private alertService: AlertService,
    private lookupService: LookupService,
    private maskService: MaskService,
  ) {}

  public ngOnInit(): void {
    this.quantidadeEntrada = this.data.ordem.quantidade;
    this.linhas = this.data.ordem.itens.map((item) => ({
      item,
      idLocalizacaoEstoque: null,
      itensRastreabilidade: [],
      rastreabilidadeCompleta: !item.isControlaLotes && !item.isControlaSeries,
    }));

    if (this.data.ordem.isControlaLotes) {
      const hoje = new Date();
      const dia = `${hoje.getDate()}`.padStart(2, '0');
      const mes = `${hoje.getMonth() + 1}`.padStart(2, '0');
      this.codigoLoteEntrada = `L-${this.data.ordem.numero}-${dia}${mes}${hoje.getFullYear()}`;
      this.dataFabricacaoLoteEntrada = hoje;
    }

    this.lookupService.getLocalizacaoEstoqueSelect().subscribe({
      next: (response) => {
        if (!response?.isError) {
          this.localizacoes = response.list ?? [];
          // O backend já devolve o local marcado como padrão em primeiro (OrderByDescending
          // isPadrao) — pré-selecionar aqui, sem precisar de um campo isPadrao na struct do
          // lookup. Usuário pode trocar livremente em qualquer um dos selects.
          const idPadrao = this.localizacoes[0]?.id ?? null;
          this.idLocalizacaoEstoqueEntrada = idPadrao;
          for (const linha of this.linhas) linha.idLocalizacaoEstoque = idPadrao;
        } else {
          this.alertService.show('Aviso', response.errorDescription || 'Não foi possível carregar os locais de estoque.', AlertType.warning);
        }
      },
      error: () => {
        this.alertService.show('Erro', 'Não foi possível carregar os locais de estoque.', AlertType.error);
      },
    });
  }

  public get quantidadeMask() {
    return this.data.ordem.isUnidadeMedidaFracionada ? this.maskService.getDynamicDecimalMask() : this.maskService.getIntegerMask();
  }

  public get delta(): number {
    return this.quantidadeEntrada - this.data.ordem.quantidade;
  }

  public get houveAjuste(): boolean {
    return Math.abs(this.delta) > 0.0001;
  }

  public onLocalizacaoInsumoChange(linha: InsumoLancamentoRow): void {
    linha.itensRastreabilidade = [];
  }

  /** `itensChange` do painel inline devolve o tipo amplo dos 4 modos possíveis (mesmo proxy usado
   * em nova-movimentacao-produto-dialog) — aqui sempre é modo "selecionar", por isso o cast. */
  public onItensRastreabilidadeChange(
    linha: InsumoLancamentoRow,
    itens: (LoteEntradaEstoqueModalItem | SerieEntradaEstoqueModalItem | LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[],
  ): void {
    linha.itensRastreabilidade = itens as (LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[];
  }

  public onItensEntradaSerieChange(
    itens: (LoteEntradaEstoqueModalItem | SerieEntradaEstoqueModalItem | LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[],
  ): void {
    this.itensEntradaSerie = itens as SerieEntradaEstoqueModalItem[];
  }

  public temRastreabilidade = (_index: number, linha: InsumoLancamentoRow): boolean =>
    linha.item.isControlaLotes || linha.item.isControlaSeries;

  public onRastreabilidadeCompletaChange(linha: InsumoLancamentoRow, completo: boolean): void {
    linha.rastreabilidadeCompleta = completo;
  }

  public gerarSeriesAutomaticamente(): void {
    const quantidade = Math.round(this.quantidadeEntrada);
    const existentes = new Set(this.itensEntradaSerie.map((s) => s.codigoSerie));
    const geradas: SerieEntradaEstoqueModalItem[] = [...this.itensEntradaSerie];
    let seq = geradas.length + 1;
    while (geradas.length < quantidade) {
      const codigo = `SN-${this.data.ordem.numero}-${seq.toString().padStart(3, '0')}`;
      if (!existentes.has(codigo)) {
        geradas.push({ codigoSerie: codigo });
        existentes.add(codigo);
      }
      seq++;
    }
    this.itensEntradaSerie = geradas;
  }

  public saldoApos(item: OrdemProducaoItemStruct): number | null {
    return item.saldoAtual === null ? null : item.saldoAtual - item.quantidadeTotal;
  }

  public get podeConfirmar(): boolean {
    if (this.isSalvando) return false;
    if (!this.idLocalizacaoEstoqueEntrada) return false;
    if (!(this.quantidadeEntrada > 0)) return false;
    if (this.houveAjuste && !this.justificativaAjusteEntrada.trim()) return false;
    if (this.linhas.some((l) => !l.idLocalizacaoEstoque)) return false;
    if (this.linhas.some((l) => !l.rastreabilidadeCompleta)) return false;
    if (this.data.ordem.isControlaLotes && (!this.codigoLoteEntrada.trim() || !this.dataFabricacaoLoteEntrada || !this.dataValidadeLoteEntrada)) return false;
    if (this.data.ordem.isControlaSeries && !this.serieEntradaCompleta) return false;
    return true;
  }

  public cancelar(): void {
    if (!this.isSalvando) this.dialogRef.close();
  }

  public confirmar(): void {
    if (!this.podeConfirmar) return;

    const request: LancarEstoqueOrdemProducaoItemRequest = {
      idOrdemProducao: this.data.ordem.idOrdemProducao,
      idLocalizacaoEstoqueEntrada: this.idLocalizacaoEstoqueEntrada!,
      quantidadeEntrada: this.quantidadeEntrada,
      justificativaAjusteEntrada: this.houveAjuste ? this.justificativaAjusteEntrada.trim() : null,
      insumos: this.linhas.map((linha) => ({
        idOrdemProducaoItem: linha.item.idOrdemProducaoItem,
        idLocalizacaoEstoque: linha.idLocalizacaoEstoque!,
        lotes: linha.item.isControlaLotes
          ? (linha.itensRastreabilidade as LoteSaidaEstoqueModalItem[])
              .filter((l) => (l.quantidadeSaida || 0) > 0)
              .map((l) => ({ idLote: l.idLote, quantidade: l.quantidadeSaida }))
          : [],
        series: linha.item.isControlaSeries
          ? (linha.itensRastreabilidade as SerieSaidaEstoqueModalItem[])
              .filter((s) => s.selecionada)
              .map((s) => ({ idSerie: s.idSerie }))
          : [],
      })),
      codigoLote: this.data.ordem.isControlaLotes ? this.codigoLoteEntrada.trim() : null,
      dataFabricacaoLote: this.data.ordem.isControlaLotes ? (this.toIso(this.dataFabricacaoLoteEntrada) ?? null) : null,
      dataValidadeLote: this.data.ordem.isControlaLotes ? (this.toIso(this.dataValidadeLoteEntrada) ?? null) : null,
      codigosSerie: this.data.ordem.isControlaSeries ? this.itensEntradaSerie.map((s) => s.codigoSerie) : [],
    };

    this.dialogRef.close(request);
  }

  private toIso(d: Date | null): string | undefined {
    if (!d) return undefined;
    const ano = d.getFullYear();
    const mes = `${d.getMonth() + 1}`.padStart(2, '0');
    const dia = `${d.getDate()}`.padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
}
